/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      // A preserved cart view can have obsolete line indexes after a successful change.
      if (this.cartNeedsRefresh) {
        const message = this.cartUpdateConfirmed ? CartItems.getRefreshError()
          : (theme.strings.cartUpdateUnconfirmedError || theme.strings.cartError);
        this.showCartError(message, null, !!this.cartUpdateConfirmed);
        return;
      }
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      let cartUpdated = false;
      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange + '.js', this.fetchRequestOpts)
                              .then(res => {
                                if (res.status === 429) {
                                  throw new Error('Too many cart requests');
                                }
                                return res;
                              })
                              .catch(err => {
                                throw new Error('Please wait a moment and try again');
                              });
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);
        if (!Number.isInteger(data.item_count) || !Array.isArray(data.items)) {
          throw new Error(theme.strings.cartUpdateUnconfirmedError || theme.strings.cartError);
        }
        cartUpdated = true;

        const newTotalQuantity = data.item_count;
        let updates;
        try {
          updates = this.prepareSectionUpdates(data.sections, newTotalQuantity === 0);
        } catch (error) {
          // Section rendering can fail after a successful mutation. Re-read once; never replay it.
          updates = await this.readSectionUpdates(newTotalQuantity === 0);
        }

        // Validate every mounted target before replacing any existing cart markup.
        updates.forEach(({ element, html }) => { element.innerHTML = html; });

        if (this.cartDrawer) {
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartSummary = document.getElementById('cart-summary');
          if (cartSummary) cartSummary.hidden = true;
        }

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        window.initLazyImages();
        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: data,
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        this.currentQuantities[variantId] = Number(quantity);

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
        }
      } catch (error) {
        const message = cartUpdated ? CartItems.getRefreshError()
          : (/^[0-9]+$/.test(error.message) ? theme.strings.cartError : error.message);
        if (cartUpdated) {
          this.cartNeedsRefresh = true;
          this.cartUpdateConfirmed = true;
        }
        this.showCartError(message, lineErrors, cartUpdated);
        console.log(error); // eslint-disable-line

        // A confirmed mutation must not be represented as rolled back in the preserved view.
        if (!cartUpdated) {
          const input = document.getElementById(`quantity-${line}`);
          if (input) {
            input.value = input.dataset.initialValue;
            const quantityInput = input.closest('quantity-input');
            if (quantityInput) quantityInput.currentQty = input.dataset.initialValue;
          }
        }
      } finally {
        this.classList.remove('pointer-events-none');
        this.querySelectorAll('.cart-item__loader').forEach((loader) => { loader.hidden = true; });
        if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'true');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = document.getElementById('cart-errors');
      try {
        const updates = await this.readSectionUpdates(this.dataset.empty === 'true');
        updates.forEach(({ element, html }) => { element.innerHTML = html; });
        this.cartNeedsRefresh = false;
        this.cartUpdateConfirmed = false;
        this.syncQuantityState();

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        window.initLazyImages();

        if (errors) {
          errors.innerHTML = '';
          errors.hidden = true;
        }
        return true;
      } catch (error) {
        this.cartNeedsRefresh = true;
        this.showCartError(this.cartUpdateConfirmed ? CartItems.getRefreshError()
          : theme.strings.cartError, errors, !!this.cartUpdateConfirmed);
        console.log(error); // eslint-disable-line
        return false;
      }
    }

    /** Build all replacements before committing them, retaining optional empty-cart markup. */
    prepareSectionUpdates(sections, emptyCart) {
      const optional = ['free-shipping-notice', 'cart-promoted-products', 'cart-drawer-media-promotion'];
      const targets = this.getSectionsToRender();
      const itemsSection = targets.find((section) => section.id === 'cart-items');
      const itemsSource = itemsSection && sections && sections[itemsSection.section];
      if (typeof itemsSource === 'string') {
        const template = document.createElement('template');
        template.innerHTML = itemsSource;
        const items = template.content.querySelector('cart-items');
        if (items) emptyCart = items.dataset.empty === 'true' || !!items.querySelector('.js-cart-empty');
      }
      const isOptional = (section) => optional.includes(section.id)
        || (emptyCart && ['cart-summary', 'cart-drawer'].includes(section.id));
      const requiredSections = new Set(targets.filter((section) => (
        document.getElementById(section.id) && !isOptional(section)
      )).map((section) => section.section));
      return targets.reduce((updates, section) => {
        const sectionEl = document.getElementById(section.id);
        if (!sectionEl) return updates;
        const source = sections && sections[section.section];
        if (typeof source !== 'string' || !source.trim()) {
          // An independent notice can fail without blocking valid cart data. A shared cart
          // section must still pass whenever it supplies a mounted required target.
          if (optional.includes(section.id) && !requiredSections.has(section.section)) return updates;
          throw new Error('Cart section unavailable');
        }
        let html = CartItems.getElementHTML(source, section.selector);
        if (html === null) {
          if (isOptional(section)) html = '';
          else throw new Error('Cart section content unavailable');
        }
        if (section.id === 'cart-items' && !html.trim()) throw new Error('Cart items content unavailable');
        updates.push({ element: sectionEl.querySelector(section.selector) || sectionEl, html });
        return updates;
      }, []);
    }

    /** One read-only recovery request, also used by explicit cart refreshes. */
    async readSectionUpdates(emptyCart) {
      const sections = [...new Set(this.getSectionsToRender().map((section) => section.section))];
      const response = await fetch(`?sections=${sections.join(',')}`);
      if (!response.ok) throw new Error(response.status);
      return this.prepareSectionUpdates(await response.json(), emptyCart);
    }

    /** Reconcile line quantities after a successful read-only refresh. */
    syncQuantityState() {
      this.currentQuantities = [];
      this.currentTotalItemCount = 0;
      const items = this.querySelectorAll('.cart-item');
      items.forEach((item) => {
        const quantity = Number(item.querySelector('.qty-input__input').value);
        this.currentQuantities[item.dataset.variantId] = quantity;
        this.currentTotalItemCount += quantity;
      });
      this.dataset.empty = items.length === 0;
      this.itemStatus = document.getElementById('cart-line-item-status');
    }

    static getRefreshError() {
      return theme.strings.cartUpdatedRefreshError
        || "Your cart was updated, but we couldn't refresh it. Please reload the cart to check the latest details.";
    }

    showCartError(message, preferred, cartUpdated = false) {
      const errors = (preferred && preferred.isConnected !== false && preferred)
        || document.getElementById('cart-errors');
      if (errors) {
        errors.textContent = message;
        errors.hidden = false;
      }
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: message, cartUpdated }
      }));
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        const summary = document.getElementById('cart-summary');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          ...(summary ? [{
            id: 'cart-summary',
            section: summary.dataset.section,
            selector: '.cart__summary'
          }] : [])
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      if (typeof html !== 'string') return null;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : null;
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}
